// frontend/src/components/CommentThread.tsx

import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import type { Comment } from "../services/comments";
import type { Document } from "../services/api";

interface DocumentColors {
  [key: number]: {
    border: string;
    badge: string;
  };
}

const DOCUMENT_COLORS: DocumentColors = {
  1: { border: "border-blue-200", badge: "bg-blue-100 text-blue-800" },
  2: { border: "border-green-200", badge: "bg-green-100 text-green-800" },
  3: { border: "border-purple-200", badge: "bg-purple-100 text-purple-800" },
};

interface CommentThreadProps {
  comment: Comment;
  documents?: Document[];
  onReplySubmit: (content: string, parentId: number) => Promise<void>;
  colorClass?: string;
}

export const CommentThread: React.FC<CommentThreadProps> = ({
  comment,
  documents,
  onReplySubmit,
  colorClass = "border-blue-100",
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Find the document index in the documents array to match ComparisonView color mapping
  const documentIndex = documents?.findIndex(doc => doc.id === comment.document_id) ?? -1;
  const colorMap: Record<number, DocumentColors[keyof DocumentColors]> = {
    0: DOCUMENT_COLORS[1], // Blue
    1: DOCUMENT_COLORS[2], // Green  
    2: DOCUMENT_COLORS[3], // Purple
  };
  const colors = colorMap[documentIndex] || DOCUMENT_COLORS[1]; // fallback to blue

  const handleSubmitReply = async () => {
    if (!replyText.trim() || isSubmitting) return;

    try {
      setIsSubmitting(true);
      await onReplySubmit(replyText, comment.id);
      setReplyText("");
      setShowReplyInput(false);
    } catch (error) {
      // Handle error appropriately
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`border-l-2 ${colorClass} pl-4 mb-4`}>
      <div className="mb-2">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-blue-900">
              {`${comment.nome} ${comment.cognome}`}
            </span>
            <span className="text-sm text-gray-500">
              {new Date(comment.created_at).toLocaleString()}
            </span>
            {documents && (
              <span
                className={`text-xs px-2 py-1 rounded-full ${colors.badge}`}
              >
                {documents.find((d) => d.id === comment.document_id)?.filename}
              </span>
            )}
          </div>
          {comment.replies?.length > 0 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          )}
        </div>

        {comment.title && (
          <h3 className="font-medium text-gray-800 mt-2 mb-1">
            {comment.title}
          </h3>
        )}
        <p className="text-gray-700">{comment.content}</p>
        <button
          className="text-blue-600 text-sm mt-1 hover:underline"
          onClick={() => setShowReplyInput(!showReplyInput)}
        >
          Rispondi
        </button>
      </div>

      {showReplyInput && (
        <div className="mb-4 ml-4">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Scrivi una risposta..."
            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
              onClick={() => setShowReplyInput(false)}
            >
              Annulla
            </button>
            <button
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              onClick={handleSubmitReply}
              disabled={!replyText.trim() || isSubmitting}
            >
              {isSubmitting ? "Invio..." : "Invia Risposta"}
            </button>
          </div>
        </div>
      )}

      {isExpanded && comment.replies?.length > 0 && (
        <div className="ml-4 space-y-3">
          {comment.replies.map((reply) => (
            <div
              key={reply.id}
              className="border-l-2 border-blue-50 pl-4 py-2 bg-gray-50 rounded-r"
            >
              <div className="mb-1">
                <span className="font-medium text-blue-900">
                  {reply.author}
                </span>
                <span className="text-sm text-gray-500 ml-2">
                  {new Date(reply.timestamp).toLocaleString()}
                </span>
              </div>
              <p className="text-gray-700">{reply.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
